import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  UserCircle2, Sparkles, AlertTriangle, ExternalLink, Loader2,
  ChevronDown, ChevronUp,
} from "lucide-react";

// Suggested Candidates, embedded inside a Coverage cell row.
// Never auto-runs. Shows the latest assignment simulation that matches the
// caller's date window, or a "Find suggested candidates" button.

const STRENGTH_META = {
  strong: { stars: "★★★", label: "Strong fit", colour: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  good:   { stars: "★★",  label: "Good fit",   colour: "text-[#1D4ED8]",   bg: "bg-[#F0F6FF]",  border: "border-[#BFD3FB]" },
  fair:   { stars: "★",   label: "Fair fit",   colour: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-200" },
};

function initials(name) {
  const parts = (name || "?").trim().split(/\s+/);
  return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
}

export default function SuggestedCandidates({ cellId, fromDate, toDate }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(null);
  const [tried, setTried] = useState(false);

  // Look up the most recent matching assignment run for this window.
  // Only triggered when the disclosure is opened (lazy + manual).
  useEffect(() => {
    if (!open || tried) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(
          `/simulations/assignment/latest?from_date=${fromDate}&to_date=${toDate}`
        );
        if (!cancelled) setSim(data);
      } catch (err) {
        if (err?.response?.status !== 404 && !cancelled) {
          toast.error(formatError(err.response?.data?.detail) || "Failed to load suggestions");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setTried(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, tried, fromDate, toDate]);

  const find = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/simulations/assignment/run", {
        from_date: fromDate, to_date: toDate,
      });
      setSim(data);
      if (data.status === "completed") {
        toast.success("Suggested candidates ready");
      } else {
        toast.error(`Failed: ${data.error_message || "see history"}`);
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to find candidates");
    } finally { setBusy(false); }
  };

  // Find the gap entry for this cell within the simulation result.
  const gap = sim?.raw_result?.gaps?.find((g) => g.cell_id === cellId);

  return (
    <div className="mt-2 rounded border border-slate-200" data-testid={`sc-block-${cellId}`}>
      <button type="button"
              className="w-full px-3 py-2 text-left text-xs flex items-center justify-between gap-2 text-[#0B1A36] hover:bg-slate-50"
              onClick={() => setOpen((o) => !o)}
              data-testid={`sc-toggle-${cellId}`}>
        <span className="inline-flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-[#2563EB]" />
          <strong>Suggested candidates</strong>
          <span className="text-[10px] text-[#64748B]">- best matches based on current setup</span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-[#64748B]" /> : <ChevronDown className="h-4 w-4 text-[#64748B]" />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100">
          {loading && (
            <div className="text-xs text-[#4B5773] flex items-center gap-2 py-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking for existing suggestions…
            </div>
          )}

          {!loading && !sim && tried && (
            <div className="text-xs text-[#4B5773] py-2">
              <p>No suggestions yet for this window. Click below to evaluate
              candidates. This is a heavier read-only check and may take a few
              seconds.</p>
              <Button onClick={find} disabled={busy}
                      data-testid={`sc-find-${cellId}`}
                      className="mt-2 bg-[#2563EB] hover:bg-[#1D4ED8] h-8 text-xs">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-2" />}
                {busy ? "Evaluating…" : "Find suggested candidates"}
              </Button>
            </div>
          )}

          {!loading && sim && !gap && (
            <div className="text-xs text-[#4B5773] py-2">
              The most recent suggestions don&apos;t include this gap. Re-run to refresh.
              <div className="mt-2">
                <Button onClick={find} disabled={busy}
                        data-testid={`sc-refresh-${cellId}`}
                        variant="outline" className="h-8 text-xs">
                  {busy ? "Evaluating…" : "Re-run suggestions"}
                </Button>
              </div>
            </div>
          )}

          {!loading && gap && (
            <CandidateList gap={gap} sim={sim} onRefresh={find} busy={busy} />
          )}
        </div>
      )}
    </div>
  );
}

function CandidateList({ gap, sim, onRefresh, busy }) {
  const [showIneligible, setShowIneligible] = useState(false);
  const cands = gap.candidates || [];
  const ineligible = gap.ineligible_summary || [];
  const ineligibleTotal = ineligible.reduce((a, b) => a + (b.count || 0), 0);

  if (cands.length === 0) {
    return (
      <div className="text-xs text-[#4B5773] py-2">
        <p>No qualified candidates were found for this gap.</p>
        {ineligibleTotal > 0 && (
          <p className="mt-1">
            {ineligibleTotal} {ineligibleTotal === 1 ? "employee was" : "employees were"} not considered ({ineligible.map((i) => i.text).join("; ")}).
          </p>
        )}
        {gap.warnings?.includes("assignment_rule_missing") && (
          <p className="mt-1 text-amber-700">
            <AlertTriangle className="inline-block h-3 w-3 mr-1" />
            No Assignment Rule defines required skills for this type of work,
            so the engine couldn&apos;t evaluate skill fit. Add a rule to improve suggestions.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] text-[#64748B]">
          {cands.length} suggested {cands.length === 1 ? "candidate" : "candidates"} for this gap.
          Best matches first.
        </p>
        <button type="button" onClick={onRefresh} disabled={busy}
                className="text-[11px] text-[#1D4ED8] hover:underline disabled:opacity-50"
                data-testid={`sc-refresh-inline-${gap.cell_id}`}>
          {busy ? "Refreshing…" : "Re-run"}
        </button>
      </div>
      {gap.warnings?.includes("assignment_rule_missing") && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"
             data-testid={`sc-rule-warn-${gap.cell_id}`}>
          <AlertTriangle className="inline-block h-3 w-3 mr-1" />
          No Assignment Rule defines required skills for this type of work,
          so all active employees qualified for skill fit. Add a rule to sharpen
          suggestions.
        </div>
      )}
      <ul className="space-y-1.5" data-testid={`sc-list-${gap.cell_id}`}>
        {cands.map((c) => <CandidateRow key={c.employee_id} c={c} cellId={gap.cell_id} date={gap.date} />)}
      </ul>
      {ineligibleTotal > 0 && (
        <div className="mt-1">
          <button type="button"
                  onClick={() => setShowIneligible((s) => !s)}
                  className="text-[11px] text-[#1D4ED8] hover:underline"
                  data-testid={`sc-ineligible-toggle-${gap.cell_id}`}>
            {showIneligible ? "Hide" : "Show"} why {ineligibleTotal} {ineligibleTotal === 1 ? "person was" : "others were"} not considered
          </button>
          {showIneligible && (
            <p className="mt-1 text-[11px] text-[#4B5773]"
               data-testid={`sc-ineligible-text-${gap.cell_id}`}>
              {ineligibleTotal} {ineligibleTotal === 1 ? "employee was" : "employees were"} not considered because they were
              {" "}{ineligible.map((i) => i.text).join(", ")}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CandidateRow({ c, cellId, date }) {
  const meta = STRENGTH_META[c.strength_band] || STRENGTH_META.fair;
  return (
    <li className="flex items-start gap-3 p-2 rounded border border-slate-100"
        data-testid={`sc-row-${cellId}-${c.employee_id}`}>
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-100 text-[#475569] flex items-center justify-center font-medium text-xs">
        {initials(c.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-medium text-[#0B1A36]">{c.name}</span>
          <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.bg} ${meta.border} ${meta.colour}`}
                data-testid={`sc-band-${c.employee_id}`}>
            {meta.stars} {meta.label}
          </span>
        </div>
        {c.reasons?.length > 0 && (
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {c.reasons.map((r, i) => (
              <li key={i} className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                ✓ {r.text}
              </li>
            ))}
          </ul>
        )}
        {c.watch_outs?.length > 0 && (
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {c.watch_outs.map((w, i) => (
              <li key={i} className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                <AlertTriangle className="inline-block h-2.5 w-2.5 mr-1" />{w.text}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-1.5">
          <a href={`/app/roster?date=${date}`}
             className="text-[11px] text-[#1D4ED8] hover:underline inline-flex items-center gap-1"
             data-testid={`sc-open-roster-${c.employee_id}`}>
            <ExternalLink className="h-3 w-3" /> Open in roster
          </a>
        </div>
      </div>
    </li>
  );
}
